import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import validator from '@rjsf/validator-ajv8';
import type { IChangeEvent } from '@rjsf/core';
import type { IFormSchema } from '~/types';
import type { RJSFSchema, ErrorSchema } from '@rjsf/utils';
import { MultiStepForm } from './MultiStepForm';
import { ShadcnForm } from './ShadcnForm';
import { PillRadioWidget } from './PillRadioWidget';
import { IframeWidget } from './IframeWidget';

// Register custom widgets
const customWidgets = {
  iframe: IframeWidget,
  radio: PillRadioWidget,
};

interface FormRendererProps {
  schema: IFormSchema;
  onSubmit: (formData: Record<string, unknown>) => void;
  onChange?: (formData: Record<string, unknown>, currentStep?: number) => void;
  initialData?: Record<string, unknown>;
  initialStep?: number;
}

export const FormRenderer = ({ schema, onSubmit, onChange, initialData, initialStep }: FormRendererProps) => {
  // Check if this is a multi-step form
  const isMultiStep = schema.steps && schema.steps.length > 0;

  if (isMultiStep) {
    return (
      <MultiStepForm
        schema={schema}
        onSubmit={onSubmit}
        onChange={onChange}
        initialData={initialData}
        initialStep={initialStep}
      />
    );
  }

  // Single-page form (backward compatible)
  return (
    <SinglePageForm
      schema={schema}
      onSubmit={onSubmit}
      onChange={onChange ? (data) => onChange(data, undefined) : undefined}
      initialData={initialData}
    />
  );
};

// Extracted single-page form logic for cleaner code
interface SinglePageFormProps {
  schema: IFormSchema;
  onSubmit: (formData: Record<string, unknown>) => void;
  onChange?: (formData: Record<string, unknown>) => void;
  initialData?: Record<string, unknown>;
}

const SinglePageForm = ({ schema, onSubmit, onChange, initialData }: SinglePageFormProps) => {
  const lastSaveRef = useRef<number>(Date.now());
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const latestFormDataRef = useRef<Record<string, unknown> | undefined>(initialData);
  const [extraErrors, setExtraErrors] = useState<ErrorSchema>({} as ErrorSchema);
  const hasSubmittedRef = useRef(false);
  const [formData, setFormData] = useState<Record<string, unknown>>(initialData || {});

  const formContext = useMemo(() => ({ formData }), [formData]);

  // Auto-save after 2 seconds of inactivity
  const handleChange = useCallback(
    (e: IChangeEvent) => {
      latestFormDataRef.current = e.formData;
      setFormData(e.formData || {});
      const now = Date.now();
      lastSaveRef.current = now;

      // Clear validation errors when user edits after a failed submit
      if (hasSubmittedRef.current) {
        setExtraErrors({} as ErrorSchema);
        hasSubmittedRef.current = false;
      }

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        if (onChange && Date.now() - lastSaveRef.current >= 2000) {
          onChange(e.formData);
        }
      }, 2000);
    },
    [onChange],
  );

  // Save on component unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Trigger final save if there's unsaved data
      if (latestFormDataRef.current && onChange) {
        onChange(latestFormDataRef.current);
      }
    };
  }, [onChange]);

  const handleSubmit = (e: IChangeEvent) => {
    // Manually validate since we use noValidate to suppress live errors
    const result = validator.validateFormData(e.formData, schema as RJSFSchema);
    if (result.errors && result.errors.length > 0) {
      hasSubmittedRef.current = true;
      // Convert RJSF errors to extraErrors format
      const errors: Record<string, { __errors: string[] }> = {};
      for (const err of result.errors) {
        const field = err.property?.replace(/^\./, '') || 'root';
        if (!errors[field]) {
          errors[field] = { __errors: [] };
        }
        errors[field].__errors.push(err.message || 'Invalid value');
      }
      setExtraErrors(errors as ErrorSchema);
      return;
    }
    onSubmit(e.formData);
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-6">
      <ShadcnForm
        schema={schema as RJSFSchema}
        uiSchema={schema.uiSchema}
        validator={validator}
        widgets={customWidgets}
        onChange={handleChange}
        onSubmit={handleSubmit}
        formData={initialData}
        formContext={formContext}
        showErrorList={false}
        noValidate
        extraErrors={extraErrors}
      >
        <button
          type="submit"
          className="w-full px-9 py-3.5 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-xl text-lg shadow-xs transition-colors duration-200"
        >
          Submit
        </button>
      </ShadcnForm>
    </div>
  );
};
